import React, { useRef } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall } from 'styles/typography';

export type TabType = {
  label: string;
  affix?: React.ReactNode;
  value: string;
  'data-cy'?: string;
  // When provided, the tab renders as a NavLink and selection is URL-driven.
  // Active state is determined by react-router rather than `selectedTab`.
  to?: string;
  // NavLink matches path prefixes by default, so a tab whose `to` is a prefix of a sibling
  // tab's URL stays highlighted on that sibling. Set for exact-match tabs.
  end?: boolean;
};

type Props = {
  tabs: readonly TabType[];
  onTabClick?: (tab: TabType) => void;
  selectedTab?: TabType;
};

export default function Tabs(props: Props) {
  const { tabs, onTabClick, selectedTab } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  const scrollTabIntoView = (event: React.MouseEvent<HTMLElement>) => {
    // `?.` on the method as well as the ref: centring the clicked tab is decoration, and where
    // Element.scrollTo isn't implemented calling it throws — taking the selection with it, since this
    // runs before onTabClick. No test clicked a tab before now, which is why that went unnoticed.
    containerRef.current?.scrollTo?.({
      left: event.currentTarget.offsetLeft - (containerRef.current.clientWidth - event.currentTarget.offsetWidth) / 2,
      behavior: 'smooth',
    });
  };

  return (
    <TabsContainer ref={containerRef} data-testid='tabs-container'>
      {tabs.map((tab) => {
        const dataCy = tab['data-cy'] || `tab-filter-${tab.value}`;

        if (tab.to) {
          return (
            <RouteTab
              key={tab.value}
              to={tab.to}
              end={tab.end}
              data-cy={dataCy}
              onClick={(event) => {
                scrollTabIntoView(event);
                onTabClick?.(tab);
              }}>
              {(isActive) => (
                <>
                  <TabText
                    color={isActive ? colors.blue100 : colors.black100}
                    fontWeight={isActive ? `${FONT_WEIGHT_MEDIUM}` : 'inherit'}>
                    {tab.label}
                  </TabText>
                  {tab.affix}
                </>
              )}
            </RouteTab>
          );
        }

        const active = selectedTab?.value === tab.value;
        return (
          <Tab
            key={tab.value}
            data-cy={dataCy}
            onClick={(event) => {
              scrollTabIntoView(event);
              onTabClick?.(tab);
            }}
            $active={active}>
            <TabText
              color={active ? colors.blue100 : colors.black100}
              fontWeight={active ? `${FONT_WEIGHT_MEDIUM}` : 'inherit'}>
              {tab.label}
            </TabText>
            {tab.affix}
          </Tab>
        );
      })}
    </TabsContainer>
  );
}

type RouteTabProps = {
  to: string;
  end?: boolean;
  'data-cy': string;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  children: (isActive: boolean) => React.ReactNode;
};

function RouteTab({ to, end, 'data-cy': dataCy, onClick, children }: RouteTabProps) {
  return (
    <NavTab to={to} end={end} data-cy={dataCy} onClick={onClick}>
      {({ isActive }) => children(isActive)}
    </NavTab>
  );
}

const tabStyles = `
  cursor: pointer;
  line-height: 20px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
`;

// 44px of height below the breakpoint: the tabs are ~40px, just under the touch minimum.
const mobileTouchTarget = css`
  @media ${({ theme }) => theme.devices.mobile} {
    min-height: 44px;
  }
`;

const Tab = styled.div<{ $active: boolean }>`
  ${tabStyles}
  padding: 10px 10px ${({ $active }) => ($active ? '7px' : '10px')} 10px;
  border-bottom: ${({ $active }) => ($active ? `3px solid ${colors.blue100}` : 'none')};
  ${mobileTouchTarget}
`;

const NavTab = styled(NavLink)`
  ${tabStyles}
  padding: 10px 10px 10px 10px;
  border-bottom: none;
  ${mobileTouchTarget}

  &.active {
    padding-bottom: 7px;
    border-bottom: 3px solid ${colors.blue100};
  }
`;

const TabsContainer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  gap: 16px;
  overflow-x: auto;

  @media ${({ theme }) => theme.devices.desktop} {
    gap: 20px;
  }
`;

const TabText = styled(BodySmall)`
  font-size: 12px;

  @media ${({ theme }) => theme.devices.desktop} {
    font-size: 14px;
  }
`;
